/* eslint-disable */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  SharedNavResponse,
  SharedNavItem,
  ConfirmationsPageContainerResponse,
  ConfirmationsPageContent,
  PageMetaDataResponse,
  TagDetail
} from '../../models/aem-graphql.model';
import { fallbackContent } from '../../../content/content-fallback';

@Injectable({
  providedIn: 'root'
})
export class CaasService {
  private pageContent: ConfirmationsPageContent;

  constructor(private readonly http: HttpClient) {}

  getContent(skipNetwork = false) {
    if (skipNetwork) {
      this.pageContent = fallbackContent;
      return Promise.resolve();
    }

    const sharedNavUrl = (environment.CONFIRMATIONS as any).SHARED_NAV_GRAPHQL_URL;
    const confirmationsUrl = (environment.CONFIRMATIONS as any).CONFIRMATIONS_GRAPHQL_URL;
    const pageMetaDataUrl = (environment.CONFIRMATIONS as any).PAGE_META_DATA_GRAPHQL_URL;

    // On the login-error route the AuthInterceptor blocks all HttpClient requests.
    // Use the native fetch() API instead so we can still load content for the nav tabs.
    if (window.location.href.includes('login-error')) {
      return Promise.allSettled([
        fetch(sharedNavUrl, { credentials: 'include' }).then(res => res.json()),
        fetch(confirmationsUrl, { credentials: 'include' }).then(res => res.json()),
        fetch(pageMetaDataUrl, { credentials: 'include' }).then(res => res.json()),
      ])
        .then(([sharedNavResult, pageContainerResult, pageMetaDataResult]) => {
          const sharedNavRaw = sharedNavResult.status === 'fulfilled' ? sharedNavResult.value : null;
          const pageContainerRaw = pageContainerResult.status === 'fulfilled' ? pageContainerResult.value : null;
          const pageMetaDataRaw = pageMetaDataResult.status === 'fulfilled' ? pageMetaDataResult.value : null;
          // Validate that responses contain expected GraphQL data structure
          const sharedNavResponse = sharedNavRaw?.data?.sharedNav ? sharedNavRaw : null;
          const pageContainerResponse = pageContainerRaw?.data?.confirmationsPageContainer ? pageContainerRaw : null;
          const pageMetaDataResponse = pageMetaDataRaw?.data?.pageMetaDataFragment ? pageMetaDataRaw : null;

          if (sharedNavResponse && pageContainerResponse) {
            if (!pageMetaDataResponse) {
              console.log('AEM pageMetaData fetch failed or returned invalid data on login-error, proceeding without SEO metadata');
            }
            this.pageContent = this.mapToPageContent(sharedNavResponse, pageContainerResponse, pageMetaDataResponse);
          } else {
            console.log('AEM GraphQL fetch failed on login-error, using fallback content');
            this.pageContent = fallbackContent;
          }
        })
        .catch((err) => {
          console.log('AEM GraphQL error on login-error:', err);
          this.pageContent = fallbackContent;
        });
    }

    return Promise.allSettled([
      this.http.get<SharedNavResponse>(sharedNavUrl).toPromise(),
      this.http.get<ConfirmationsPageContainerResponse>(confirmationsUrl).toPromise(),
      this.http.get<PageMetaDataResponse>(pageMetaDataUrl).toPromise(),
    ]).then(([sharedNavResult, pageContainerResult, pageMetaDataResult]) => {
      const sharedNavRaw = sharedNavResult.status === 'fulfilled' ? sharedNavResult.value : null;
      const pageContainerRaw = pageContainerResult.status === 'fulfilled' ? pageContainerResult.value : null;
      const pageMetaDataRaw = pageMetaDataResult.status === 'fulfilled' ? pageMetaDataResult.value : null;
      // Validate that responses contain expected GraphQL data structure
      const sharedNavResponse = sharedNavRaw?.data?.sharedNav ? sharedNavRaw : null;
      const pageContainerResponse = pageContainerRaw?.data?.confirmationsPageContainer ? pageContainerRaw : null;
      const pageMetaDataResponse = pageMetaDataRaw?.data?.pageMetaDataFragment ? pageMetaDataRaw : null;

      if (sharedNavResponse && pageContainerResponse) {
        if (!pageMetaDataResponse) {
          console.log('AEM pageMetaData request failed or returned invalid data, proceeding without SEO metadata');
        }
        this.pageContent = this.mapToPageContent(sharedNavResponse, pageContainerResponse, pageMetaDataResponse);
      } else {
        console.log('AEM GraphQL requests failed, using fallback content');
        this.pageContent = fallbackContent;
      }
    }).catch((err) => {
      console.log('AEM GraphQL error:', err);
      this.pageContent = fallbackContent;
    });
  }

  getPageContent(): ConfirmationsPageContent {
    return this.pageContent;
  }

  /**
   * @deprecated Use getPageContent() instead. Kept for backward compatibility during migration.
   */
  getCaasData(): any {
    if (!this.pageContent) {
      return null;
    }
    return {
      Data: {
        confirms: {
          content: {
            heading: this.pageContent.heading,
            confirmsDescription: this.pageContent.confirmsDescription,
            tabOptions: this.pageContent.tabOptions,
            dropdownOptions: this.pageContent.dropdownOptions,
            browserTitle: this.pageContent.seoBrowserTitle,
            apiErrorHeading: this.pageContent.errorMessages.systemUnavailableHeading,
            apiErrorBody: this.pageContent.errorMessages.noConfirmationsInPeriod,
            disclaimer: this.pageContent.disclaimer,
            title: 'Confirmations',
            duplicatedStatementLabel: this.pageContent.dropdownOptions?.['option_01']?.optionLabel || 'Duplicate statements and transaction confirmations',
            notesAndDisclosures: '',
          }
        }
      }
    };
  }

  private mapToPageContent(
    sharedNavResponse: SharedNavResponse | null,
    pageContainerResponse: ConfirmationsPageContainerResponse | null,
    pageMetaDataResponse: PageMetaDataResponse | null
  ): ConfirmationsPageContent {
    // --- Shared Nav ---
    const rawChildren = sharedNavResponse?.data?.sharedNav?.item?.children;
    let navItems: SharedNavItem[] = [];
    if (Array.isArray(rawChildren)) {
      navItems = rawChildren;
    } else if (rawChildren && typeof rawChildren === 'object') {
      navItems = Object.values(rawChildren);
    }

    const downloadCenterItem = navItems.find(item => (item._path || '').includes('download-center'));
    const downloadCenterLink = {
      label: downloadCenterItem?.labelText || environment.downloadAccountLink.label,
      url: downloadCenterItem?.url || environment.downloadAccountLink.url,
    };

    const nonTabKeywords = ['download-center', 'print-link', 'create-report'];
    const tabItems = navItems
      .filter(item => {
        const path = item._path || '';
        return !nonTabKeywords.some(keyword => path.includes(keyword)) && item.labelText;
      })
      .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));

    const tabOptions: { [key: string]: { optionLabel: string; optionUrl?: string } } = {};
    tabItems.forEach((item, index) => {
      const tabKey = `tab-option-${String(index + 1).padStart(2, '0')}`;
      const isCurrentApp = (item._path || '').includes('confirmations-tab');
      tabOptions[tabKey] = {
        optionLabel: item.labelText,
        optionUrl: isCurrentApp ? undefined : (item.url || undefined),
      };
    });

    // --- Confirmations Page Container ---
    const pageItem = pageContainerResponse?.data?.confirmationsPageContainer?.item;

    const heading = pageItem?.heading || 'All accounts';
    const bodyRaw = pageItem?.body || [];
    const bodyItems: any[] = Array.isArray(bodyRaw) ? bodyRaw : Object.values(bodyRaw);
    const confirmsDescription = bodyItems[0]?.html || '<p>Confirmations are available for the current year and the previous 5 years.</p>';

    // View dropdown (primaryCta)
    const primaryCta = pageItem?.primaryCta;
    const viewDropdownLabel = primaryCta?.heading || 'View';
    const viewDropdownPlaceholder = primaryCta?.body?.html?.replace(/<\/?p>/g, '') || 'Select view';
    const viewChildrenRaw = primaryCta?.children || [];
    const viewChildren: any[] = Array.isArray(viewChildrenRaw) ? viewChildrenRaw : Object.values(viewChildrenRaw);
    const dropdownOptions: { [key: string]: { optionLabel: string; optionUrl?: string } } = {};
    viewChildren.forEach((child, index) => {
      const key = `option_${String(index + 1).padStart(2, '0')}`;
      dropdownOptions[key] = {
        optionLabel: child.labelText || '',
        optionUrl: child.url || undefined,
      };
    });

    // Secondary CTA (Show more / Show less buttons)
    const secondaryCta = pageItem?.secondaryCta;
    const secondaryChildrenRaw = secondaryCta?.children || [];
    const secondaryChildren: any[] = Array.isArray(secondaryChildrenRaw) ? secondaryChildrenRaw : Object.values(secondaryChildrenRaw);
    const showMoreItem = secondaryChildren.find((c: any) => (c._path || '').includes('show-more'));
    const showLessItem = secondaryChildren.find((c: any) => (c._path || '').includes('show-less'));
    const sendItem = secondaryChildren.find((c: any) => (c._path || '').includes('int-send'));
    const showMoreLabel = showMoreItem?.labelText || 'Show more';
    const showLessLabel = showLessItem?.labelText || 'Show less';
    const sendLabel = sendItem?.labelText || 'Send';

    const backButtonItem = secondaryChildren.find((c: any) => (c._path || '').includes('back-button'));
    const continueButtonItem = secondaryChildren.find((c: any) => (c._path || '').includes('continue-button'));
    const submitButtonItem = secondaryChildren.find((c: any) => (c._path || '').includes('submit-button'));
    const backToConfirmationsButtonItem = secondaryChildren.find((c: any) => (c._path || '').includes('back-to-confirmations-button'));
    const changeStatusButtonItem = secondaryChildren.find((c: any) => (c._path || '').includes('int-change-status-button'));
    const quitButtonItem = secondaryChildren.find((c: any) => (c._path || '').includes('quit-button'));
    const okButtonItem = secondaryChildren.find((c: any) => (c._path || '').includes('ok-button'));

    // Additional CTA (Update table button)
    const additionalCta = pageItem?.additionalCta;
    const updateTableLabel = additionalCta?.labelText || 'Update Table';

    // Table column labels
    const tables = pageItem?.tables || [];
    const tablesArr: any[] = Array.isArray(tables) ? tables : Object.values(tables);
    const columnLabelsRaw = tablesArr[0]?.columnLabels || [];
    const columnLabels: any[] = Array.isArray(columnLabelsRaw) ? columnLabelsRaw : Object.values(columnLabelsRaw);
    const tableSection: { [key: string]: string } = {};
    columnLabels.forEach((col) => {
      const path = col._path || '';
      if (path.includes('date-label')) tableSection['dateColumnLabel'] = col.labelText;
      else if (path.includes('confirmation-type-label')) tableSection['confirmationTypeColumnLabel'] = col.labelText;
      else if (path.includes('description-label')) tableSection['descriptionColumnLabel'] = col.labelText;
      else if (path.includes('download-label')) tableSection['viewDownloadColumnLabel'] = col.labelText;
      else if (path.includes('media-type-label')) tableSection['mediaTypeColumnLabel'] = col.labelText;
      else if (path.includes('view-label')) tableSection['viewLabel'] = col.labelText;
      else if (path.includes('internal-only-label')) tableSection['internalOnlyLabel'] = col.labelText;
      else if (path.includes('int-status-label')) tableSection['statusColumnLabel'] = col.labelText;
      else if (path.includes('int-duplicate-label')) tableSection['duplicateColumnLabel'] = col.labelText;
    });

    // Form fields (Year, Months, Confirmation type labels)
    const formContainers = pageItem?.form || [];
    const formArr: any[] = Array.isArray(formContainers) ? formContainers : Object.values(formContainers);
    const formFieldsRaw = formArr[0]?.formFields || [];
    const formFields: any[] = Array.isArray(formFieldsRaw) ? formFieldsRaw : Object.values(formFieldsRaw);
    const yearField = formFields.find((f: any) => (f._path || '').includes('year'));
    const monthsField = formFields.find((f: any) => (f._path || '').includes('month'));
    const confirmTypeField = formFields.find((f: any) => (f._path || '').includes('confirmation-type'));

    const formFieldsContent = {
      yearLabel: yearField?.labelText || 'Year',
      yearAltText: yearField?.altText || 'select year',
      monthsLabel: monthsField?.labelText || 'Months',
      monthsPlaceholder: monthsField?.placeholder || 'All months',
      monthsAltText: monthsField?.altText || 'select month',
      confirmationTypeLabel: confirmTypeField?.labelText || 'Confirmation type',
      confirmationTypePlaceholder: confirmTypeField?.placeholder || 'All confirmation types',
      confirmationTypeAltText: confirmTypeField?.altText || 'select confirmation type',
    };

    // Error messages
    const messagesRaw = pageItem?.messages || [];
    const messages: any[] = Array.isArray(messagesRaw) ? messagesRaw : Object.values(messagesRaw);
    let noConfirmationsInPeriod = 'There are no confirmations available for this time period. Please use the dropdown to make another selection.';
    let dataFetchError = 'Unable to fetch data. Try again later.';
    let systemUnavailableHeading = 'Data unavailable';
    let systemUnavailableBody: string[] = [
      'Our system is temporarily unable to display some of your account information. We apologize for the inconvenience and appreciate your patience while we fix these issues.',
      'Please refresh in a few minutes or choose another page.'
    ];

    messages.forEach((msg: any) => {
      const path = msg._path || '';
      const bodyRawMsg = msg.body || [];
      const bodyArr: any[] = Array.isArray(bodyRawMsg) ? bodyRawMsg : Object.values(bodyRawMsg);
      if (path.includes('no-confirmations-in-this-period')) {
        noConfirmationsInPeriod = bodyArr[0]?.html?.replace(/<\/?p>/g, '').trim() || noConfirmationsInPeriod;
      } else if (path.includes('data-fetch-error')) {
        dataFetchError = bodyArr[0]?.html?.replace(/<\/?p>/g, '').replace(/&nbsp;/g, ' ').trim() || dataFetchError;
      } else if (path.includes('system-unable-to-display')) {
        systemUnavailableHeading = msg.heading || systemUnavailableHeading;
        systemUnavailableBody = bodyArr.map((b: any) => b.html?.replace(/<\/?p>/g, '').trim()).filter(Boolean);
      }
    });

    const labelsRaw = pageItem?.labels || [];
    const labels: any[] = Array.isArray(labelsRaw) ? labelsRaw : Object.values(labelsRaw);
    const findLabelByPath = (fragment: string): string => {
      const item = labels.find((label: any) => (label._path || '').includes(fragment));
      return item?.labelText || '';
    };

    const childrenRaw = pageItem?.children || [];
    const headerChildren: any[] = Array.isArray(childrenRaw) ? childrenRaw : Object.values(childrenRaw);
    const selectRecipientHeader = headerChildren.find((child: any) => (child._path || '').includes('int-select-recipient-header'));
    const reviewSubmitHeader = headerChildren.find((child: any) => (child._path || '').includes('int-review-submit-header'));
    const confirmationHeader = headerChildren.find((child: any) => (child._path || '').includes('int-confirmation-header'));

    const crewOnlyMessageItem = messages.find((msg: any) => (msg._path || '').includes('int-crew-only-page-internal-callout'));
    const statusUpdatedItem = messages.find((msg: any) => (msg._path || '').includes('int-status-updated-success-message'));
    const deletePermanentItem = messages.find((msg: any) => (msg._path || '').includes('int-delete-is-permanent-warning-message'));

    const crewOnlyBodyRaw = crewOnlyMessageItem?.body || [];
    const crewOnlyBody: any[] = Array.isArray(crewOnlyBodyRaw) ? crewOnlyBodyRaw : Object.values(crewOnlyBodyRaw);
    const deleteBodyRaw = deletePermanentItem?.body || [];
    const deleteBody: any[] = Array.isArray(deleteBodyRaw) ? deleteBodyRaw : Object.values(deleteBodyRaw);

    const confirmationJourneyContent = {
      internalOnlyHeading: crewOnlyMessageItem?.heading || tableSection['internalOnlyLabel'] || 'Internal only',
      crewOnlyMessage: crewOnlyBody[0]?.html?.replace(/<\/?p>/g, '').trim() || 'This page is for crew only. Clients cannot view this page.',
      sendDuplicateConfirmationLabel: findLabelByPath('int-send-duplicate-confirmation-label') || 'Send duplicate confirmation',
      stepProgressTemplate: findLabelByPath('int-send-duplicate-progress-indicator') || 'Step ${step-number} of 3',
      stepLabels: {
        selectRecipient: selectRecipientHeader?.heading || 'Select recipient',
        reviewSubmit: reviewSubmitHeader?.heading || 'Review & submit',
        confirmation: confirmationHeader?.heading || 'Confirmation',
      },
      headers: {
        selectRecipient: selectRecipientHeader?.heading || 'Select recipient',
        reviewSubmit: reviewSubmitHeader?.heading || 'Review & submit',
        confirmation: confirmationHeader?.heading || 'Confirmation',
      },
      descriptions: {
        statusReview: reviewSubmitHeader?.subheading || 'Confirm your confirmation status changes before submitting.',
        confirmationSubmitted: confirmationHeader?.subheading || 'Your duplicate confirmation request has been submitted.',
      },
      tableLabels: {
        confirmationDate: findLabelByPath('int-confirmation-date-label') || 'Confirmation date',
        account: findLabelByPath('int-account-label') || 'Account',
        recipient: findLabelByPath('int-recipient-label') || 'Recipient',
        date: findLabelByPath('labels/date-label') || 'Date',
        confirmation: findLabelByPath('int-confirmation-label') || 'Confirmation',
        status: findLabelByPath('int-status-label') || 'Status',
      },
      buttons: {
        back: backButtonItem?.labelText || 'Back',
        continue: continueButtonItem?.labelText || 'Continue',
        submit: submitButtonItem?.labelText || 'Submit',
        backToConfirmations: backToConfirmationsButtonItem?.labelText || 'Back to confirmations',
        quit: quitButtonItem?.labelText || 'Quit',
        ok: okButtonItem?.labelText || 'OK',
        changeStatus: changeStatusButtonItem?.labelText || 'Change status',
      },
      statusReviewMessages: {
        deletePermanentHeading: deletePermanentItem?.heading || 'Deleting a confirmation is permanent',
        deletePermanentBody: deleteBody[0]?.html?.replace(/<\/?p>/g, '').trim() || 'You will not be able to change the status again.',
        statusUpdatedHeading: statusUpdatedItem?.heading || 'Confirmation status updated',
      },
    };

    // Disclosure / disclaimer
    const disclosuresRaw = pageItem?.disclosureFootnoteHelp || [];
    const disclosures: any[] = Array.isArray(disclosuresRaw) ? disclosuresRaw : Object.values(disclosuresRaw);
    let disclaimer = '';
    if (disclosures.length > 0) {
      const disclosure = disclosures[0];
      const disclosureBodyRaw = disclosure.body || [];
      const disclosureBodyArr = Array.isArray(disclosureBodyRaw) ? disclosureBodyRaw : Object.values(disclosureBodyRaw);
      let disclosureHtml = disclosureBodyArr[0]?.html || '';
      if (disclosure.primaryCta) {
        disclosureHtml = disclosureHtml.replace(
          '${finra-org-link}',
          `<a href="${disclosure.primaryCta.url}" target="_blank"><b>${disclosure.primaryCta.labelText}</b></a>`
        );
      }
      if (disclosure.secondaryCta) {
        disclosureHtml = disclosureHtml.replace(
          '${sipc-org-link}',
          `<a href="${disclosure.secondaryCta.url}" target="_blank"><b>${disclosure.secondaryCta.labelText}</b></a>`
        );
      }
      disclaimer = disclosureHtml;
    }

    // --- Page Meta Data ---
    const metaDataItem = pageMetaDataResponse?.data?.pageMetaDataFragment?.items?.[0];
    const tagDetails: TagDetail[] = pageMetaDataResponse?.tagDetails || [];

    const resolveTagTitle = (tagName: string): string => {
      const detail = tagDetails.find(t => t.name === tagName);
      return detail?.title || tagName;
    };

    const primaryKeywordTitles = (metaDataItem?.primaryMetaKeywords || []).map(resolveTagTitle);
    const secondary = metaDataItem?.secondaryMetaKeywords || '';
    const keywordsMetaContent = secondary
      ? primaryKeywordTitles.join(', ') + ', ' + secondary
      : primaryKeywordTitles.join(', ');

    const contentTypeTitles = (metaDataItem?.contentType || []).map(resolveTagTitle);
    const contentFormatTitles = (metaDataItem?.contentFormat || []).map(resolveTagTitle);

    const excludeSet = new Set([...(metaDataItem?.contentType || []), ...(metaDataItem?.contentFormat || [])]);
    const topicsMetaContent = (metaDataItem?.primaryMetaKeywords || [])
      .filter(name => !excludeSet.has(name))
      .join(', ');

    return {
      heading,
      confirmsDescription,
      tabOptions,
      dropdownOptions,
      viewDropdownLabel,
      viewDropdownPlaceholder,
      showMoreLabel,
      showLessLabel,
      sendLabel,
      updateTableLabel,
      tableSection,
      formFields: formFieldsContent,
      errorMessages: {
        noConfirmationsInPeriod,
        dataFetchError,
        systemUnavailableHeading,
        systemUnavailableBody,
      },
      disclaimer,
      notesAndDisclosures: '',
      downloadCenterLink,
      seoBrowserTitle: metaDataItem?.seoBrowserTitle || 'Confirmations | Vanguard',
      seoMetaDescription: metaDataItem?.seoMetaDescription || 'View your transaction confirmations.',
      enableNoindexRobots: metaDataItem?.enableNoindexRobots ?? false,
      enableNofollowRobots: metaDataItem?.enableNofollowRobots ?? false,
      keywordsMetaContent,
      contentTypeMetaContent: contentTypeTitles.join(', '),
      contentFormatMetaContent: contentFormatTitles.join(', '),
      topicsMetaContent,
      aiModel: metaDataItem?.aiModel || null,
      confirmationJourneyContent,
    };
  }
}
